import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    calendarOutline,
    chevronForwardOutline,
    locationOutline,
    refreshOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionDetail,
    CompetitionGame,
    Team,
    getCompetitionDetail,
    getHomeData
} from "../services/mexfbaApi";
import { isCompletedGame } from "../utils/competitionStandings";
import "./Calendar.css";

interface RouteParams {
    id: string;
}

type CalendarFilter = "todos" | "proximos" | "finalizados";

interface CalendarDateGroup {
    dateKey: string;
    games: CompetitionGame[];
}

function parseDate(
    date?: string
): Date | null {
    if (!date) {
        return null;
    }

    const normalizedDate =
        date.length === 10
            ? `${date}T12:00:00`
            : date;

    const parsedDate = new Date(normalizedDate);

    return Number.isNaN(parsedDate.getTime())
        ? null
        : parsedDate;
}


type TimedCompetitionGame = CompetitionGame & {
    hora?: string;
};

function getGameHour(
    game: CompetitionGame
): string {
    const timedGame = game as TimedCompetitionGame;

    return String(timedGame.hora || "").trim();
}

function hasConfirmedGameHour(
    game: CompetitionGame
): boolean {
    return /^\d{1,2}:\d{2}(?::\d{2})?$/.test(
        getGameHour(game)
    );
}

function parseGameDateTime(
    game: CompetitionGame
): Date | null {
    const date = String(game.fecha || "").trim();

    if (!date) {
        return null;
    }

    const hour = hasConfirmedGameHour(game)
        ? getGameHour(game)
        : "12:00:00";

    const normalizedHour =
        hour.length === 5
            ? `${hour}:00`
            : hour;

    const parsedDate = new Date(
        `${date}T${normalizedHour}`
    );

    return Number.isNaN(parsedDate.getTime())
        ? null
        : parsedDate;
}

function hasNumericScore(
    value?: string | number
): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}

function formatGameHour(
    game: CompetitionGame
): string {
    if (!hasConfirmedGameHour(game)) {
        return "Hora por confirmar";
    }

    return getGameHour(game).slice(0, 5);
}

function getGameStatus(
    game: CompetitionGame
): "Final" | "Próximo" | "Pendiente" {
    if (isCompletedGame(game)) {
        return "Final";
    }

    const dateTime = parseGameDateTime(game);

    if (!dateTime) {
        return "Pendiente";
    }

    /*
        Si ya pasó la hora efectiva, pero el resultado no existe
        o no es numérico, el encuentro sigue pendiente.
    */
    if (dateTime.getTime() <= Date.now()) {
        return "Pendiente";
    }

    return "Próximo";
}

function getPhaseLabel(game: CompetitionGame): string {
    if (game.partido === "grupos") {
        return game.jornada
            ? `Jornada ${game.jornada}`
            : "Fase de grupos";
    }

    if (game.partido === "1") {
        return "Final";
    }

    if (game.partido === "3") {
        return "Semifinal";
    }

    if (game.partido === "5") {
        return "Wild Card";
    }

    if (game.partido === "7") {
        return "Play-in";
    }
    return game.partido || "Partido";
}

function getDateKey(game: CompetitionGame): string {
    const date = parseDate(game.fecha);

    if (!date) {
        return "sin-fecha";
    }

    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0")
    ].join("-");
}

function formatCalendarDate(dateKey: string): string {
    if (dateKey === "sin-fecha") {
        return "Fecha por confirmar";
    }

    const date = parseDate(`${dateKey}T12:00:00`);

    if (!date) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
    }).format(date);
}

function formatSeasonDate(date?: string): string {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

const Calendar: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [competition, setCompetition] =
        useState<CompetitionDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [filter, setFilter] =
        useState<CalendarFilter>("todos");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadCalendar = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [competitionData, homeData] =
                    await Promise.all([
                        getCompetitionDetail(id, forceRefresh),
                        getHomeData(forceRefresh)
                    ]);

                setCompetition(competitionData);
                setTeams(homeData.teams);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar el calendario:",
                    requestError
                );

                setError(
                    "No fue posible cargar los partidos de esta competencia."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadCalendar();
        },
        [loadCalendar]
    );

    const teamLogos = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team.logo || ""];
            })
        );
    }, [teams]);

    const allGames = useMemo(function (): CompetitionGame[] {
        if (!competition) {
            return [];
        }

        const gamesById = new Map<string, CompetitionGame>();

        [
            ...Object.values(competition.Partidos || {}),
            ...Object.values(competition.Final || {})
        ].forEach(function (game) {
            if (!game?.id) {
                return;
            }

            gamesById.set(String(game.id), game);
        });

        return Array.from(gamesById.values()).sort(
            function (firstGame, secondGame) {
const firstDate =
    parseGameDateTime(firstGame)?.getTime() || 0;

const secondDate =
    parseGameDateTime(secondGame)?.getTime() || 0;


                return firstDate - secondDate;
            }
        );
    }, [competition]);

    const filteredGames = useMemo(function () {
        return allGames.filter(function (game) {
            if (filter === "proximos") {
                return !isCompletedGame(game);
            }

            if (filter === "finalizados") {
                return isCompletedGame(game);
            }

            return true;
        });
    }, [allGames, filter]);

    const calendarGroups = useMemo<CalendarDateGroup[]>(
        function () {
            const groups = new Map<string, CompetitionGame[]>();

            filteredGames.forEach(function (game) {
                const dateKey = getDateKey(game);
                const currentGames = groups.get(dateKey) || [];

                currentGames.push(game);
                groups.set(dateKey, currentGames);
            });

            return Array.from(groups.entries())
                .sort(function ([firstDate], [secondDate]) {
                    if (firstDate === "sin-fecha") {
                        return 1;
                    }

                    if (secondDate === "sin-fecha") {
                        return -1;
                    }

                    return firstDate.localeCompare(secondDate);
                })
                .map(function ([dateKey, games]) {
                    return {
                        dateKey,
                        games
                    };
                });
        },
        [filteredGames]
    );

    function getTeamLogo(teamId: string): string {
        return teamLogos.get(String(teamId)) || "";
    }

    function openGame(gameId: string) {
        history.push(`/game/${gameId}`);
    }

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadCalendar(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Calendario</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="calendar-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !competition) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Calendario</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="calendar-content">
                    <div className="calendar-empty">
                        <IonIcon icon={calendarOutline} />
                        <strong>No fue posible abrir el calendario</strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadCalendar(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = competition.Info;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="calendar-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar a la competencia"
                            onClick={function () {
                                history.push(`/results/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Calendario</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar calendario"
                            disabled={refreshing}
                            onClick={function () {
                                loadCalendar(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="calendar-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando calendario..."
                    />
                </IonRefresher>

                <section className="calendar-hero">
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="calendar-hero__placeholder">
                            <IonIcon icon={trophyOutline} />
                        </div>
                    )}

                    <div>
                        <span>CALENDARIO DE COMPETENCIA</span>
                        <h1>{info.nombre}</h1>
                        <p>
                            {info.fechaI
                                ? `${formatSeasonDate(
                                      info.fechaI
                                  )} — ${formatSeasonDate(info.fechaF)}`
                                : "Temporada sin fechas confirmadas"}
                        </p>
                    </div>
                </section>

                <section className="calendar-navigation">
                    <IonButton>Calendario</IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/results/${id}`);
                        }}
                    >
                        Tabla
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsResults/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeamResults/${id}`);
                        }}
                    >
                        Estadísticas
                    </IonButton>
                </section>

                <section className="calendar-section">
                    <div className="calendar-heading">
                        <div>
                            <span>PARTIDOS REGISTRADOS</span>
                            <h2>{filteredGames.length} encuentros</h2>
                        </div>

                        <IonIcon icon={calendarOutline} />
                    </div>

                    <IonSegment
                        className="calendar-filter"
                        value={filter}
                        onIonChange={function (event) {
                            setFilter(
                                (event.detail.value ||
                                    "todos") as CalendarFilter
                            );
                        }}
                    >
                        <IonSegmentButton value="todos">
                            Todos
                        </IonSegmentButton>

                        <IonSegmentButton value="proximos">
                            Pendientes
                        </IonSegmentButton>

                        <IonSegmentButton value="finalizados">
                            Finalizados
                        </IonSegmentButton>
                    </IonSegment>

                    {calendarGroups.length === 0 ? (
                        <div className="calendar-no-results">
                            <IonIcon icon={calendarOutline} />
                            <strong>No hay partidos en esta sección</strong>
                            <span>
                                Prueba con otro filtro o vuelve a intentarlo
                                más tarde.
                            </span>
                        </div>
                    ) : (
                        <div className="calendar-list">
                            {calendarGroups.map(function (group) {
                                return (
                                    <section
                                        className="calendar-date-group"
                                        key={group.dateKey}
                                    >
                                        <h3>
                                            {formatCalendarDate(
                                                group.dateKey
                                            )}
                                        </h3>

                                        <div className="calendar-games">
                                            {group.games.map(
                                                function (game) {
                                                    const completed =
                                                        isCompletedGame(game);

                                                    const confirmedHour = hasConfirmedGameHour(game);

                                                    const localLogo =
                                                        getTeamLogo(
                                                            game.id_local
                                                        );

                                                    const visitorLogo =
                                                        getTeamLogo(
                                                            game.id_visita
                                                        );

                                                    const status =
                                                        getGameStatus(game);

                                                    return (
                                                        <button
                                                            className={`calendar-game ${
                                                                completed
                                                                    ? "calendar-game--completed"
                                                                    : ""
                                                            }`}
                                                            key={game.id}
                                                            type="button"
                                                            onClick={function () {
                                                                openGame(
                                                                    game.id
                                                                );
                                                            }}
                                                        >
                                                            <div className="calendar-game__top">
                                                                <span>
                                                                    {getPhaseLabel(
                                                                        game
                                                                    )}
                                                                </span>

                                                                <IonBadge
                                                                    color={
                                                                        completed
                                                                            ? "success"
                                                                            : status ===
                                                                              "Pendiente"
                                                                            ? "warning"
                                                                            : "primary"
                                                                    }
                                                                >
                                                                    {status}
                                                                </IonBadge>
                                                            </div>

                                                            <div className="calendar-game__match">
                                                                <div className="calendar-game__team calendar-game__team--local">
                                                                    <span>
                                                                        {game.nombre_local ||
                                                                            "Equipo local"}
                                                                    </span>

                                                                    {localLogo ? (
                                                                        <img
                                                                            src={
                                                                                localLogo
                                                                            }
                                                                            alt=""
                                                                        />
                                                                    ) : (
                                                                        <div className="calendar-game__team-placeholder">
                                                                            {game.nombre_local
                                                                                ?.charAt(
                                                                                    0
                                                                                )
                                                                                .toUpperCase() ||
                                                                                "L"}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="calendar-game__score">
                                                                    {completed ? (
                                                                        <>
                                                                            <strong>
                                                                                {game.resLocal ??
                                                                                    "0"}
                                                                            </strong>

                                                                            <span>
                                                                                -
                                                                            </span>

                                                                            <strong>
                                                                                {game.resVisita ??
                                                                                    "0"}
                                                                            </strong>
                                                                        </>
                                                                    ) : (
                                                                        <span
            className={[
                "calendar-game__time",
                !confirmedHour
                    ? "calendar-game__time--pending"
                    : ""
            ].join(" ")}
        >
            {confirmedHour ? (
                formatGameHour(game)
            ) : (
                <>
                    Hora
                    <br />
                    por confirmar
                </>
            )}
        </span>
                                                                    )}
                                                                </div>

                                                                <div className="calendar-game__team calendar-game__team--visitor">
                                                                    {visitorLogo ? (
                                                                        <img
                                                                            src={
                                                                                visitorLogo
                                                                            }
                                                                            alt=""
                                                                        />
                                                                    ) : (
                                                                        <div className="calendar-game__team-placeholder">
                                                                            {game.nombre_visita
                                                                                ?.charAt(
                                                                                    0
                                                                                )
                                                                                .toUpperCase() ||
                                                                                "V"}
                                                                        </div>
                                                                    )}

                                                                    <span>
                                                                        {game.nombre_visita ||
                                                                            "Equipo visitante"}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="calendar-game__bottom">
                                                                <span>
                                                                    <IonIcon
                                                                        icon={
                                                                            locationOutline
                                                                        }
                                                                    />
                                                                    {game.sede ||
                                                                        "Sede por confirmar"}
                                                                </span>

                                                                <IonIcon
                                                                    icon={
                                                                        chevronForwardOutline
                                                                    }
                                                                />
                                                            </div>
                                                        </button>
                                                    );
                                                }
                                            )}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Calendar;
